import { useEffect, useState } from 'react';
import {
  CelebrityEventPopularShefsDocument,
  CelebrityEventPopularShefsQuery,
  DishFirstExploreSegmentShefFragment,
  DishFirstFoodItemsSegmentFragment,
  ExploreFoodItemsSegmentLayout,
  OrderShefFragment,
} from 'src/gqlReactTypings.generated.d';
import { ISegmentMetrics } from 'src/pages/consumer/explore/SegmentAnalytics';
import { UseDishFirstExploreUrl } from 'src/pages/consumer/explore/useExploreUrl';
import { useGqlClient } from 'src/shared/useGqlClient';
import { useCachedZipCode } from 'src/user-preferences/useCachedZipCode';

const CAROUSEL_TITLE = 'Find the perfect meal';
const CAROUSEL_SUBTITLE = 'Earn free delivery when you order from multiple shefs';

const safeFilters = (filters: UseDishFirstExploreUrl) => ({
  availabilityDates: filters.dateFilters,
  cuisineFilters: filters.cuisineFilters,
  dietaryFilters: filters.dietaryFilters,
  dishTypeFilters: filters.dishTypeFilters,
  isEcoFriendlyPackaging: filters.ecoFilter,
  minDishRating: filters.minRatingFilter,
  priceLevelFilters: filters.priceLevelFilters,
  proteinFilters: filters.proteinFilters,
  segmentFilters: filters.segmentFilters,
  spiceLevelFilters: filters.spiceLevelFilters,
  browseFilter: filters.browseFilter,
});
// Helper to wrap list of shefs in a displayable segment structure
const createSegmentAndMetrics = (
  shefId: string,
  exploreSegmentShefs: DishFirstExploreSegmentShefFragment[]
): [DishFirstFoodItemsSegmentFragment | null, ISegmentMetrics | null] => {
  const segment = {
    cacheKey: `celebrityEventPopularShefs_${shefId}`,
    id: 'celebrityEventPopularShefs',
    layout: ExploreFoodItemsSegmentLayout.Generic,
    displayName: CAROUSEL_TITLE,
    subtitle: CAROUSEL_SUBTITLE,
    content: {
      items: exploreSegmentShefs,
      total: exploreSegmentShefs.length,
    },
  };
  const segmentMetrics = {
    esid: segment.id,
    pos: 1,
    totalItems: segment.content.total,
    renderedItems: segment.content.items.length,
  };
  return [segment, segmentMetrics];
};

interface IPopularShefsHookProps {
  shef: OrderShefFragment;
  filters: UseDishFirstExploreUrl;
}

export const useCelebrityEventUpsellSegmentData = ({
  shef,
  filters,
}: IPopularShefsHookProps): [
  DishFirstFoodItemsSegmentFragment | null,
  ISegmentMetrics | null,
  DishFirstExploreSegmentShefFragment[]
] => {
  const GqlClient = useGqlClient();
  const [zip] = useCachedZipCode();
  const zipCode = zip ?? shef?.shefProfile?.randomZip;

  const [shefs, setShefs] = useState<DishFirstExploreSegmentShefFragment[]>([]);
  const [segment, setSegment] = useState<DishFirstFoodItemsSegmentFragment | null>(null);
  const [segmentMetrics, setSegmentMetrics] = useState<ISegmentMetrics | null>(null);

  useEffect(() => {
    let isMounted = true;

    const fetchPopularShefs = async () => {
      const { data } = await GqlClient.query<CelebrityEventPopularShefsQuery>({
        query: CelebrityEventPopularShefsDocument,
        variables: { zipCode, filters: safeFilters(filters) },
      });

      const [newSegment, newSegmentMetrics] = createSegmentAndMetrics(shef.id, data.celebrityEventPopularShefs);

      if (isMounted) {
        setShefs(data.celebrityEventPopularShefs ?? []);
        setSegment(newSegment);
        setSegmentMetrics(newSegmentMetrics);
      }
    };

    if (filters && shef.id) fetchPopularShefs();

    // For handling unmounts before api call is complete
    return () => {
      isMounted = false;
    };
  }, [GqlClient, filters, shef.id]);

  return [segment, segmentMetrics, shefs];
};
